import React, { useState } from 'react';
import { ArrowLeft, Check, X, Zap, Star, Crown, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { SeoHead } from '../components/SeoHead';

const PRICES = {
  essenziale:    { monthly: 300,  annual: 3600  },
  professional:  { monthly: 750,  annual: 9000  },
  elite:         { monthly: 1500, annual: 18000 },
};

const PLAN_ICONS = {
  essenziale:   Zap,
  professional: Star,
  elite:        Crown,
};

const PLAN_COLORS = {
  essenziale:   { accent: '#a67c52', light: 'rgba(166,124,82,0.12)', border: 'rgba(166,124,82,0.35)' },
  professional: { accent: '#c9a84c', light: 'rgba(201,168,76,0.12)', border: 'rgba(201,168,76,0.4)' },
  elite:        { accent: '#e5d4a1', light: 'rgba(229,212,161,0.12)', border: 'rgba(229,212,161,0.4)' },
};

// Feature comparison table data
const FEATURES = [
  { category: 'Base' },
  { label: 'Proprietà',              values: ['1', 'Fino a 5', 'Illimitate'] },
  { label: 'Admin utenti',           values: ['1', '3', 'Illimitati'] },
  { label: 'Portale prenotazioni',   values: [true, true, true] },
  { label: 'Guest App PWA',          values: [true, true, true] },
  { label: 'Chiavi virtuali',        values: ['Max 10', 'Illimitate', 'Illimitate'] },
  { label: 'AI Booking Assistant',   values: [true, true, true] },
  { label: 'GDPR strumenti',         values: [true, true, true] },

  { category: 'Professional' },
  { label: 'Smart Home (18 brand)',  values: [false, '5 brand · 25 disp.', '18 brand · Illimitati'] },
  { label: 'Portale Fornitori + PDF',values: [false, true, true] },
  { label: 'Export CSV Ricavi',      values: [false, true, true] },
  { label: 'Rate Calendar Heatmap',  values: [false, true, true] },
  { label: 'Log Accessi Dettagliato',values: [false, true, true] },
  { label: 'Multi-Proprietà',        values: [false, true, true] },

  { category: 'Elite Exclusive' },
  { label: 'i18n Editor testi',      values: [false, false, true] },
  { label: 'White-label 100%',       values: [false, false, true] },
  { label: 'Accesso API',            values: [false, false, true] },
  { label: 'Onboarding assistito',   values: [false, false, true] },

  { category: 'Supporto' },
  { label: 'Canale supporto',        values: ['Email', 'Prioritario', 'Dedicato'] },
  { label: 'SLA risposta',           values: ['48h', '24h', '4h'] },
  { label: 'Aggiornamenti futuri',   values: [true, true, true] },
];

const PLANS = ['essenziale', 'professional', 'elite'];
const PLAN_LABELS = { essenziale: 'Essenziale', professional: 'Professional', elite: 'Elite' };
const PLAN_DESC = {
  essenziale:   'Per strutture singola proprietà che cercano automazione chiavi e prenotazioni.',
  professional: 'Per chi gestisce più strutture con smart home e ha bisogno di tutti gli strumenti.',
  elite:        'Per strutture luxury che non accettano compromessi — white-label, illimitato.',
};

function ValueCell({ value }) {
  if (value === true) return (
    <div className="flex justify-center">
      <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'rgba(5,150,105,0.15)' }}>
        <Check size={11} style={{ color: '#059669' }} strokeWidth={2.5} />
      </div>
    </div>
  );
  if (value === false) return (
    <div className="flex justify-center">
      <X size={14} style={{ color: 'rgba(100,100,100,0.5)' }} strokeWidth={1.5} />
    </div>
  );
  return <div className="text-center text-xs" style={{ color: 'var(--lux-text-muted)' }}>{value}</div>;
}

export default function PricingPage() {
  const [billingAnnual] = useState(true);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const handleDemo = () => {
    window.location.href = '/#demo';
  };
  const handleContact = () => {
    window.open('mailto:info@shangrilux.com?subject=Shangrilux%20Pricing%20Inquiry', '_blank');
  };

  const FAQS = [
    { q: 'Come funziona la demo gratuita?', a: 'Compili il form nella homepage, ricevi credenziali immediate. Hai 24 ore per esplorare un ambiente completo con dati di esempio — proprietà, tariffe, prenotazione demo, tutti i pannelli attivi. Si cancella automaticamente.' },
    { q: 'Posso cambiare piano in qualsiasi momento?', a: 'Sì. L\'upgrade è immediato — le nuove funzioni si attivano al pagamento. Il downgrade avviene al rinnovo annuale. Tutti i dati vengono preservati.' },
    { q: 'Cosa include il piano Lifetime?', a: 'Il piano Lifetime include tutte le funzionalità Elite in modo permanente, senza canone annuale. Il prezzo è personalizzato. Contattaci per ricevere la tua offerta dedicata.' },
    { q: 'La fatturazione è davvero annuale?', a: 'Sì. Il prezzo mensile mostrato è il costo mensile equivalente — l\'importo viene addebitato una volta sola all\'anno. Ricezione fattura elettronica.' },
    { q: 'Smart home: quali brand sono supportati?', a: 'Con Professional: Nuki, Tuya, Home Assistant, Loxone, BTicino (5 brand). Con Elite: tutti i 18 brand — KNX, Somfy, Yale, Schlage, Tedee, Salto KS, dormakaba, Fibaro, Nice, MyQ, Webhook, MQTT, Modbus.' },
    { q: 'Cosa succede se non rinnovo?', a: '30 giorni di grazia dalla scadenza: il servizio rimane attivo. Dopo 30 giorni senza rinnovo, l\'account viene disattivato e i dati rimossi definitivamente.' },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#080808', color: '#e5e5e5', fontFamily: "'DM Sans', sans-serif" }}>
      <SeoHead page="pricing" lang="it" theme="dark" />

      {/* Top nav */}
      <div className="sticky top-0 z-40 border-b" style={{ background: 'rgba(8,8,8,0.95)', backdropFilter: 'blur(20px)', borderColor: '#1a1a1a' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3 text-sm hover:opacity-80 transition-opacity"
            style={{ color: 'rgba(229,229,229,0.6)' }}>
            <ArrowLeft size={15} />
            <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '18px', fontWeight: 300, letterSpacing: '0.05em', color: '#e5e5e5' }}>
              Shangrilux
            </span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/#demo" className="text-sm px-4 py-2 rounded-sm font-medium"
              style={{ background: '#c9a84c', color: '#000' }}>
              Demo Gratuita
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6">

        {/* Hero */}
        <div className="pt-20 pb-16 text-center">
          <p className="text-xs tracking-[5px] uppercase mb-5"
            style={{ color: '#c9a84c' }}>Prezzi</p>
          <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 'clamp(36px,5vw,64px)', fontWeight: 300, lineHeight: 1.1, marginBottom: '16px' }}>
            Prezzi chiari.{' '}
            <span style={{ color: '#c9a84c' }}>Nessuna sorpresa.</span>
          </h1>
          <p className="text-base mx-auto max-w-lg" style={{ color: 'rgba(229,229,229,0.55)', lineHeight: 1.7 }}>
            Tutti i piani includono aggiornamenti futuri, pannello admin completo e portale prenotazioni. Fatturazione annuale.
          </p>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-20">
          {PLANS.map((plan, idx) => {
            const Icon = PLAN_ICONS[plan];
            const col = PLAN_COLORS[plan];
            const p = PRICES[plan];
            const isPopular = plan === 'professional';

            return (
              <div key={plan} data-testid={`pricing-card-${plan}`}
                className="relative rounded-sm flex flex-col"
                style={{
                  background: isPopular ? 'rgba(201,168,76,0.06)' : '#0f0f0f',
                  border: `1px solid ${isPopular ? 'rgba(201,168,76,0.5)' : '#1e1e1e'}`,
                }}>

                {/* Badge area — always rendered to keep card tops aligned */}
                <div className="h-9 flex items-center justify-center pt-2">
                  {isPopular && (
                    <span className="px-4 py-1 text-[10px] font-bold tracking-widest uppercase rounded-full"
                      style={{ background: '#c9a84c', color: '#000' }}>
                      Più scelto
                    </span>
                  )}
                </div>
                <div className="px-7 pb-7 pt-3 flex flex-col flex-1">

                {/* Plan header */}
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: col.light }}>
                    <Icon size={15} style={{ color: col.accent }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm" style={{ color: '#e5e5e5' }}>
                      {PLAN_LABELS[plan]}
                    </h3>
                  </div>
                </div>

                <p className="text-xs mb-6 leading-relaxed" style={{ color: 'rgba(229,229,229,0.45)' }}>
                  {PLAN_DESC[plan]}
                </p>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-end gap-2">
                    <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '42px', fontWeight: 300, color: col.accent, lineHeight: 1 }}>
                      €{p.monthly}
                    </span>
                    <span className="text-xs pb-1.5" style={{ color: 'rgba(229,229,229,0.4)' }}>/mese</span>
                  </div>
                  <p className="text-[11px] mt-1" style={{ color: 'rgba(229,229,229,0.35)' }}>
                    €{p.annual} fatturati annualmente
                  </p>
                </div>

                <button
                  onClick={handleDemo}
                  data-testid={`cta-${plan}`}
                  className="w-full py-2.5 text-sm font-semibold rounded-sm transition-opacity hover:opacity-85"
                  style={{
                    background: isPopular ? '#c9a84c' : col.light,
                    color: isPopular ? '#000' : col.accent,
                    border: `1px solid ${isPopular ? 'transparent' : col.border}`,
                  }}>
                  Attiva demo gratuita
                </button>
                <p className="text-[10px] text-center mt-2 mb-6" style={{ color: 'rgba(229,229,229,0.25)' }}>
                  Nessuna carta richiesta · Attivo in 2 minuti
                </p>

                {/* Key features for this plan */}
                <div className="pt-5 space-y-2.5" style={{ borderTop: '1px solid #1a1a1a' }}>
                  {FEATURES.filter(f => !f.category && f.values[idx] !== false).map((f, fi) => (
                    <div key={fi} className="flex items-start gap-2.5">
                      <Check size={12} className="mt-0.5 flex-shrink-0" style={{ color: col.accent }} />
                      <span className="text-xs" style={{ color: 'rgba(229,229,229,0.65)' }}>
                        {f.label}{typeof f.values[idx] === 'string' ? ` — ${f.values[idx]}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Lifetime CTA */}
        <div className="rounded-sm p-8 mb-20 text-center"
          style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.2)' }}>
          <p className="text-[10px] tracking-[4px] uppercase mb-3" style={{ color: '#c9a84c' }}>Su richiesta</p>
          <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '28px', fontWeight: 300, marginBottom: '10px' }}>
            Piano Lifetime — accesso permanente
          </h2>
          <p className="text-sm mb-6 max-w-lg mx-auto" style={{ color: 'rgba(229,229,229,0.5)', lineHeight: 1.7 }}>
            Tutte le funzioni Elite, per sempre, pagamento una tantum. Prezzo personalizzato su richiesta.
            Ideale per strutture luxury che vogliono eliminare costi ricorrenti.
          </p>
          <button onClick={handleContact} data-testid="lifetime-cta"
            className="inline-flex items-center gap-2 px-7 py-3 text-sm font-semibold rounded-sm"
            style={{ background: '#c9a84c', color: '#000' }}>
            Richiedi offerta Lifetime
            <ArrowRight size={14} />
          </button>
        </div>

        {/* Feature comparison table */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <p className="text-[10px] tracking-[4px] uppercase mb-3" style={{ color: '#c9a84c' }}>Confronto completo</p>
            <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '32px', fontWeight: 300 }}>
              Tutte le funzionalità nel dettaglio.
            </h2>
          </div>

          <div className="overflow-x-auto rounded-sm" style={{ border: '1px solid #1a1a1a' }}>
            <table className="w-full" style={{ borderCollapse: 'collapse' }}>
              {/* Header */}
              <thead>
                <tr style={{ borderBottom: '1px solid #1e1e1e' }}>
                  <th className="text-left px-6 py-4 text-xs font-normal"
                    style={{ color: 'rgba(229,229,229,0.35)', width: '38%' }}>
                    Funzionalità
                  </th>
                  {PLANS.map(plan => {
                    const col = PLAN_COLORS[plan];
                    const isPopular = plan === 'professional';
                    return (
                      <th key={plan} data-testid={`table-header-${plan}`}
                        className="px-4 py-4 text-center"
                        style={{
                          background: isPopular ? 'rgba(201,168,76,0.05)' : 'transparent',
                          borderLeft: `1px solid #1a1a1a`,
                        }}>
                        <span className="text-xs font-semibold" style={{ color: col.accent }}>
                          {PLAN_LABELS[plan]}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {FEATURES.map((row, ri) => {
                  if (row.category) {
                    return (
                      <tr key={ri} style={{ background: '#0c0c0c', borderTop: '1px solid #1a1a1a', borderBottom: '1px solid #1a1a1a' }}>
                        <td colSpan={4} className="px-6 py-2.5">
                          <span className="text-[10px] font-semibold tracking-[3px] uppercase"
                            style={{ color: 'rgba(201,168,76,0.6)' }}>
                            {row.category}
                          </span>
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={ri}
                      className="transition-colors"
                      style={{ borderBottom: '1px solid #141414' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#0e0e0e'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td className="px-6 py-3.5">
                        <span className="text-sm" style={{ color: 'rgba(229,229,229,0.75)' }}>
                          {row.label}
                        </span>
                      </td>
                      {PLANS.map((plan, idx) => (
                        <td key={plan} className="px-4 py-3.5"
                          style={{
                            borderLeft: '1px solid #141414',
                            background: plan === 'professional' ? 'rgba(201,168,76,0.03)' : 'transparent',
                          }}>
                          <ValueCell value={row.values[idx]} />
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {/* Price row */}
                <tr style={{ borderTop: '2px solid #1e1e1e', background: '#0c0c0c' }}>
                  <td className="px-6 py-5">
                    <span className="text-sm font-semibold" style={{ color: '#e5e5e5' }}>
                      Prezzo annuale
                    </span>
                  </td>
                  {PLANS.map(plan => {
                    const col = PLAN_COLORS[plan];
                    return (
                      <td key={plan} className="px-4 py-5 text-center"
                        style={{
                          borderLeft: '1px solid #1a1a1a',
                          background: plan === 'professional' ? 'rgba(201,168,76,0.05)' : 'transparent',
                        }}>
                        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '22px', fontWeight: 300, color: col.accent }}>
                          €{PRICES[plan].annual}
                        </div>
                        <div className="text-[10px] mt-0.5" style={{ color: 'rgba(229,229,229,0.35)' }}>
                          €{PRICES[plan].monthly}/mese equiv.
                        </div>
                      </td>
                    );
                  })}
                </tr>
                {/* CTA row */}
                <tr>
                  <td className="px-6 py-5" />
                  {PLANS.map(plan => {
                    const col = PLAN_COLORS[plan];
                    const isPopular = plan === 'professional';
                    return (
                      <td key={plan} className="px-4 py-5"
                        style={{
                          borderLeft: '1px solid #141414',
                          background: isPopular ? 'rgba(201,168,76,0.05)' : 'transparent',
                        }}>
                        <button
                          onClick={handleDemo}
                          data-testid={`table-cta-${plan}`}
                          className="w-full py-2.5 text-sm font-semibold rounded-sm transition-opacity hover:opacity-85"
                          style={{
                            background: isPopular ? '#c9a84c' : col.light,
                            color: isPopular ? '#000' : col.accent,
                            border: `1px solid ${isPopular ? 'transparent' : col.border}`,
                          }}>
                          Attiva demo gratuita
                        </button>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-20 max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[10px] tracking-[4px] uppercase mb-3" style={{ color: '#c9a84c' }}>FAQ</p>
            <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '28px', fontWeight: 300 }}>
              Domande frequenti.
            </h2>
          </div>
          <div className="space-y-2">
            {FAQS.map((item, i) => (
              <div key={i} data-testid={`faq-${i}`}
                className="rounded-sm overflow-hidden"
                style={{ border: '1px solid #1a1a1a' }}>
                <button
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left text-sm"
                  style={{ color: '#e5e5e5' }}>
                  <span>{item.q}</span>
                  {expandedFaq === i
                    ? <ChevronUp size={15} style={{ color: '#c9a84c', flexShrink: 0 }} />
                    : <ChevronDown size={15} style={{ color: 'rgba(229,229,229,0.4)', flexShrink: 0 }} />}
                </button>
                {expandedFaq === i && (
                  <div className="px-6 pb-5 text-sm leading-relaxed"
                    style={{ color: 'rgba(229,229,229,0.55)', borderTop: '1px solid #1a1a1a', paddingTop: '16px' }}>
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="pb-20 text-center">
          <p className="text-sm mb-6" style={{ color: 'rgba(229,229,229,0.5)' }}>
            Non sei sicuro di quale piano fa per te?
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="/#demo"
              className="inline-flex items-center gap-2 px-8 py-3 text-sm font-semibold rounded-sm"
              style={{ background: '#c9a84c', color: '#000' }}>
              Demo Gratuita 24h
              <ArrowRight size={14} />
            </a>
            <button onClick={handleContact}
              className="inline-flex items-center gap-2 px-8 py-3 text-sm font-semibold rounded-sm"
              style={{ border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c' }}>
              Contattaci
            </button>
          </div>
        </div>

      </div>

      {/* Footer minimal */}
      <footer className="border-t py-10 px-6"
        style={{ borderColor: '#1a1a1a' }}>
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row flex-wrap items-center justify-between gap-4">
          <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '16px', fontWeight: 300, color: 'rgba(229,229,229,0.3)' }}>Shangrilux</span>
          <div className="flex flex-wrap gap-5 justify-center">
            {[['/', 'Home'], ['/blog', 'Guide'], ['/marketplace', 'Marketplace'], ['/developers', 'API']].map(([h, l]) => (
              <a key={h} href={h} className="text-xs hover:opacity-70 transition-opacity" style={{ color: 'rgba(229,229,229,0.3)' }}>{l}</a>
            ))}
          </div>
          <div className="flex flex-wrap gap-5 justify-center">
            {[['/privacy', 'Privacy'], ['/cookie', 'Cookie'], ['/terms', 'Termini']].map(([h, l]) => (
              <a key={h} href={h} className="text-xs hover:opacity-70 transition-opacity" style={{ color: 'rgba(229,229,229,0.25)' }}>{l}</a>
            ))}
          </div>
          <p className="text-xs" style={{ color: 'rgba(229,229,229,0.2)' }}>
            © {new Date().getFullYear()} Shangrilux · Design by{' '}
            <a href="https://francescorusso.work" target="_blank" rel="noreferrer"
              className="hover:opacity-70 transition-opacity"
              style={{ color: 'rgba(201,168,76,0.6)' }}>
              Francesco Russo
            </a>
            {' '}·{' '}
            <a href="https://shangrilux.com" target="_blank" rel="noreferrer"
              className="hover:opacity-70 transition-opacity"
              style={{ color: 'rgba(201,168,76,0.6)' }}>
              Shangrilux
            </a>
          </p>
        </div>
      </footer>

    </div>
  );
}
